/**
 * So, we're making our own dayjs wrapper that defaults to currentTimezone.
 * This timezone should get set in the `/_authenticated/$orgId` layout
 * component where we load the tenant organization.
 *
 * You should 100% import this everywhere
 * DON'T do: `import dayjs from "dayjs"`.
 * Instead use this line:
 * import dayjs from '@/lib/dayjs'
 *
 * Additionally, don't use `new Date()` anywhere.
 * Instead, use `dayjs()` and `dayjs().toDate()` if you need a normal JavaScript Date object
 *
 * If you ever need something that is really in the local timezone of the browser...
 * you'd better leave a comment explaining why, or we'll think it was a mistake!
 *
 * Also, you can now add any plugins that you need here (in one place)
 * to be available everywhere throughout the application.
 *
 * WARN: One danger in using this approach is parsing dates that
 * are in UTC, but are not formatted as such. For example, if you have a date like:
 * "2025-12-01T20:55:44.000000Z" // the default returned from Laravel when cast as `datetime`
 * Then everything works perfectly. Basically, anything with a "Z" or a "+XX:00" at the end is great.
 * It will be interpreted as UTC and then converted to the current timezone.
 * But, if you don't cast as a `datetime` then you'll get:
 * ""2025-12-01 20:55:44"" // The string actually stored in the database column
 * This is bad news! It will be parsed as if it were already in the local timezone!
 * Either cast as `datetime` in Laravel or manually use `dayjs.utc(theDateString)` to parse.
 */
import dayjsBase, { type ConfigType, type Dayjs } from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjsBase.extend(utc)
dayjsBase.extend(timezone)
dayjsBase.extend(advancedFormat)
dayjsBase.extend(customParseFormat)

let currentTimezone = 'America/Chicago' // same default as the API

const MINUTE_MS = 60_000
const DAY_MS = 24 * 60 * MINUTE_MS

export function setCurrentTimezone(tz: string | null | undefined) {
  currentTimezone = tz || 'America/Chicago'
  dayjsBase.tz.setDefault(currentTimezone)
}

// Helper to determine if a string will be parsed as UTC or not
function isUtcString(dateString: string) {
  // Ending with Z / z or ±HH:mm or ±HHmm
  return /([zZ]|[+-]\d{2}:?\d{2})$/.test(dateString)
}

const zoneFormatters = new Map<string, Intl.DateTimeFormat>()

// A zone's UTC offset in minutes at an instant, read from Intl so the computer's timezone can't leak in.
function zoneOffsetMinutes(timeZone: string, instantMs: number): number {
  let formatter = zoneFormatters.get(timeZone)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    })
    zoneFormatters.set(timeZone, formatter)
  }

  const parts = formatter.formatToParts(new Date(instantMs))
  const field = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value)
  const wallAsUtcMs = Date.UTC(
    field('year'),
    field('month') - 1,
    field('day'),
    field('hour'),
    field('minute'),
    field('second')
  )

  return Math.round((wallAsUtcMs - Math.floor(instantMs / 1000) * 1000) / MINUTE_MS)
}

/**
 * A clock time in `timeZone` ("2027-03-14", "2027-03-14 09:00"). Day.js's own `dayjs.tz` builds the
 * value by shifting the computer's clock, so near the computer's DST change it reads an hour off:
 * on a Central computer, "2027-03-14" in Los Angeles came back as Mar 13, 11 PM. This finds the
 * offset with Intl instead, and keeps the clock time when Day.js's `.tz()` misjudges the offset.
 */
function parseInZone(input: string, format: string | undefined, timeZone: string): Dayjs {
  const wall = format ? dayjsBase.utc(input, format) : dayjsBase.utc(input)

  if (!wall.isValid()) {
    return format ? dayjsBase.tz(input, format, timeZone) : dayjsBase.tz(input, timeZone)
  }

  const wallMs = wall.valueOf()
  // The zone's offsets a day either side, so a DST change near the clock time shows as two.
  const before = zoneOffsetMinutes(timeZone, wallMs - DAY_MS)
  const after = zoneOffsetMinutes(timeZone, wallMs + DAY_MS)
  const fits = (offset: number) =>
    zoneOffsetMinutes(timeZone, wallMs - offset * MINUTE_MS) === offset
  // As JavaScript's own Date and Temporal read them: a clock time the fall change repeats takes its
  // first occurrence, and one the spring change skips moves forward past the gap.
  const offset = fits(before) || !fits(after) ? before : after
  const instantMs = wallMs - offset * MINUTE_MS
  const instantOffset = zoneOffsetMinutes(timeZone, instantMs)
  const zoned = dayjsBase(instantMs).tz(timeZone)

  return zoned.utcOffset() === instantOffset ? zoned : zoned.utcOffset(instantOffset, true)
}

// Wrapper function: behaves like dayjs(), but fixed to currentTimezone
function dayjs(input?: ConfigType, format?: string, strict?: boolean): Dayjs {
  // Case 1: No input (e.g. `dayjs()`) - return the current date in the default timezone
  if (input === null || input === undefined) {
    return dayjsBase().tz(currentTimezone)
  }

  // Case 2: Non-string input (e.g., dayjs object, Date object, or numeric timestamp)
  if (typeof input !== 'string') {
    return dayjsBase(input, format, strict).tz(currentTimezone)
  }

  // Case 3: UTC-formatted string - parse it as UTC and convert to currentTimezone
  if (isUtcString(input)) {
    return dayjsBase.utc(input, format, strict).tz(currentTimezone)
  }

  // Case 4: Non-UTC-formatted string (e.g., "2025-12-01") - interpret as already in the default timezone
  // (`dayjs.tz` has no strict mode, so strict parsing doesn't apply here.)
  return parseInZone(input, format, currentTimezone)
}

/**
 * `dayjs.tz(value, [format], timezone)`: a string is a clock time in `timezone` (read with
 * `parseInZone`, so it doesn't depend on the computer's timezone), unless it carries its own UTC
 * offset ("…Z", "…-06:00"); that, and anything that isn't a string, is an instant.
 */
function tz(input?: ConfigType, timezone?: string): Dayjs
function tz(input: ConfigType, format: string, timezone?: string): Dayjs
function tz(input?: ConfigType, ...rest: (string | undefined)[]): Dayjs {
  // Three arguments always mean a format, even when the timezone passed is undefined.
  const [format, timezone] = rest.length > 1 ? rest : [undefined, rest[0]]
  const zone = timezone || currentTimezone

  if (typeof input !== 'string') {
    return dayjsBase(input).tz(zone)
  }

  return isUtcString(input)
    ? dayjsBase.utc(input, format).tz(zone)
    : parseInZone(input, format, zone)
}
tz.guess = dayjsBase.tz.guess
tz.setDefault = dayjsBase.tz.setDefault

function getTimezoneName(tz: string) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    timeZoneName: 'long',
  })
    .formatToParts(new Date())
    .find((part) => part.type === 'timeZoneName')?.value
}

// Re-expose anything else used directly on dayjs (not an "instance" of dayjs)
// Note, you shouldn't really need `extend()`, just add plugins to this file above.
dayjs.utc = dayjsBase.utc
dayjs.tz = tz
dayjs.isDayjs = dayjsBase.isDayjs
dayjs.extend = dayjsBase.extend

// Convenience alias
dayjs.plugin = dayjsBase.extend
dayjs.getTimezoneName = getTimezoneName

export default dayjs
